import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Tabs, Typography } from 'antd';
import { Container, Button } from '../../../../../core/components';
import JobForm from './JobForm';
import RecommendationsTable from './RecommendationsTable';
import { JobService } from '../services/job.service';
import { Job as JobModel, RecommendationCandidate } from '../models/job.model';
import { CloseCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { TablePaginationConfig } from 'antd/es/table';
import { SorterResult, FilterValue } from 'antd/es/table/interface';
import { route } from '../../../../../constants/routes';

const DEFAULT_RECOMMENDATIONS_SORT = '-relevance';

const Job: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const fromDashboard = location.state?.fromDashboard;
  const [activeTab, setActiveTab] = useState(() => {
    return location.state?.activeTab || 'job';
  });
  const [recommendationsTotal, setRecommendationsTotal] = useState(0);
  const [recommendationsData, setRecommendationsData] = useState<
    RecommendationCandidate[]
  >([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [recommendationsLoaded, setRecommendationsLoaded] = useState(false);
  const [recommendationsError, setRecommendationsError] = useState<
    string | null
  >(null);
  const [item, setItem] = useState<JobModel>(() => new JobModel());
  const [recommendationsPagination, setRecommendationsPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [recommendationsSort, setRecommendationsSort] = useState<string>(
    DEFAULT_RECOMMENDATIONS_SORT
  );
  const [recommendationsFilter, setRecommendationsFilter] = useState<string>();
  const [recommendationsSortField, setRecommendationsSortField] =
    useState('relevance');
  const [recommendationsSortOrder, setRecommendationsSortOrder] = useState<
    'ascend' | 'descend' | null
  >('descend');

  useEffect(() => {
    let ignore = false;

    const loadJob = async () => {
      if (!id) return;
      try {
        const job: JobModel = await JobService.GetJob(id);
        if (!ignore) {
          setItem(job);
        }
      } catch (error: unknown) {
        if (ignore) return;
        console.error('Error fetching job data:', error);
      }
    };

    void loadJob();

    return () => {
      ignore = true;
    };
  }, [id]);

  const getRecommendations = useCallback(async () => {
    if (!id) return;

    setRecommendationsLoading(true);
    setRecommendationsError(null);

    try {
      const params = {
        page: recommendationsPagination.current,
        size: recommendationsPagination.pageSize,
        sort: recommendationsSort || DEFAULT_RECOMMENDATIONS_SORT,
        ...(recommendationsFilter ? { filter: recommendationsFilter } : {}),
      };

      const response = await JobService.GetRecommendations(id, params);
      setRecommendationsTotal(response?.totalElements ?? 0);
      setRecommendationsData(response?.content ?? []);
      setRecommendationsLoaded(true);
    } catch (error: unknown) {
      console.error('Error fetching recommendations:', error);
      setRecommendationsTotal(0);
      setRecommendationsData([]);
      setRecommendationsLoaded(true);
      setRecommendationsError(
        'Could not load recommendations. Check your connection and try again.'
      );
    } finally {
      setRecommendationsLoading(false);
    }
  }, [
    id,
    recommendationsPagination,
    recommendationsSort,
    recommendationsFilter,
  ]);

  useEffect(() => {
    if (id) {
      void getRecommendations();
    }
  }, [id, getRecommendations]);

  const handleTabChange = (activeKey: string) => {
    setActiveTab(activeKey);
    if (activeKey === 'recommendations') {
      void getRecommendations();
    }
  };

  const handleRecommendationsChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<RecommendationCandidate>
      | SorterResult<RecommendationCandidate>[]
  ) => {
    const filterParts: string[] = [];
    for (const key in filters) {
      const value = filters[key]?.[0];
      if (value) {
        filterParts.push(`${key}:like:${value}`);
      }
    }
    const filterString =
      filterParts.length > 0 ? filterParts.join(',') : undefined;
    setRecommendationsFilter(filterString);

    if (!Array.isArray(sorter) && sorter.order && sorter.field) {
      const direction = sorter.order === 'ascend' ? '' : '-';
      const field = String(sorter.field);
      setRecommendationsSort(`${direction}${field}`);
      setRecommendationsSortField(field);
      setRecommendationsSortOrder(sorter.order);
    } else {
      setRecommendationsSort(DEFAULT_RECOMMENDATIONS_SORT);
      setRecommendationsSortField('relevance');
      setRecommendationsSortOrder('descend');
    }

    setRecommendationsPagination({
      current: paginationConfig.current!,
      pageSize: paginationConfig.pageSize!,
    });
  };

  const goBack = useCallback(() => {
    if (fromDashboard) {
      navigate(route('/dashboard'));
    } else {
      navigate(route('/job'));
    }
  }, [fromDashboard, navigate]);

  const copyLink = useCallback(async () => {
    const url = item.url;

    if (url) {
      try {
        await navigator.clipboard.writeText(url);
      } catch (error) {
        console.error('Error copying link:', error);
      }
    }
  }, [item.url]);

  const jobSkillCount = item.skills?.length ?? 0;

  const handleJobUpdate = useCallback((job: JobModel) => {
    setItem(job);
  }, []);

  const JobFormContent = useMemo(
    () => <JobForm onJobUpdate={handleJobUpdate} />,
    [handleJobUpdate]
  );

  const RecommendationsContent = useMemo(
    () => (
      <Container>
        <div className="mx-auto space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-1">
              <h2 className="jo-heading text-xl font-semibold">
                Recommendations
              </h2>
              <Typography.Text
                type="secondary"
                className="jo-body-muted block text-sm"
              >
                {jobSkillCount > 0
                  ? `${jobSkillCount} job skill${jobSkillCount === 1 ? '' : 's'} · best fit first`
                  : 'Add skills on Details to enable matching'}
              </Typography.Text>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              {item.id ? (
                <Button
                  icon={{
                    value: <LinkOutlined />,
                    size: 'text-lg',
                  }}
                  color="blue"
                  text={{
                    size: 'md',
                    value: 'Job Link',
                    weight: 500,
                  }}
                  tone={500}
                  title="Copy job link"
                  btnClass="w-full sm:w-auto"
                  onClick={() => void copyLink()}
                />
              ) : null}
              <Button
                icon={{
                  value: <CloseCircleOutlined />,
                  size: 'text-lg',
                }}
                color="gray"
                text={{
                  size: 'md',
                  value: 'Close',
                  weight: 500,
                }}
                tone={500}
                title="Back to jobs"
                btnClass="w-full sm:w-auto"
                onClick={goBack}
              />
            </div>
          </div>

          <div className="jo-callout">
            <p className="text-sm font-medium jo-heading">
              Skill-based recommendations
            </p>
            <p className="jo-body-muted mt-1 text-sm leading-relaxed">
              Candidate fit uses the latest AI Brief score when available and
              profile skill overlap before they apply. Both use the same bands
              as <span className="font-medium jo-heading">Check fit</span> on
              the public job page:{' '}
              <span className="font-medium jo-heading">Strong ≥75%</span>,{' '}
              <span className="font-medium jo-heading">Partial ≥45%</span>. The
              score source is shown on each candidate.
            </p>
          </div>

          <RecommendationsTable
            data={recommendationsData}
            total={recommendationsTotal}
            jobName={item.name || item.jobName}
            jobSkillCount={jobSkillCount}
            loading={recommendationsLoading}
            loaded={recommendationsLoaded}
            errorMessage={recommendationsError}
            onRetry={() => void getRecommendations()}
            hasActiveFilter={Boolean(recommendationsFilter)}
            sortField={recommendationsSortField}
            sortOrder={recommendationsSortOrder}
            pagination={recommendationsPagination}
            onChange={handleRecommendationsChange}
          />
        </div>
      </Container>
    ),
    [
      recommendationsData,
      recommendationsTotal,
      recommendationsLoading,
      recommendationsLoaded,
      recommendationsError,
      recommendationsFilter,
      recommendationsSortField,
      recommendationsSortOrder,
      recommendationsPagination,
      item,
      jobSkillCount,
      getRecommendations,
      copyLink,
      goBack,
    ]
  );

  const items = useMemo(
    () => [
      {
        key: 'job',
        label: 'Details',
        children: JobFormContent,
      },
      ...(id
        ? [
            {
              key: 'recommendations',
              label: `Recommendations (${recommendationsLoading && !recommendationsLoaded ? '…' : recommendationsTotal})`,
              children: RecommendationsContent,
            },
          ]
        : []),
    ],
    [
      id,
      recommendationsTotal,
      recommendationsLoading,
      recommendationsLoaded,
      JobFormContent,
      RecommendationsContent,
    ]
  );

  if (!id) {
    return JobFormContent;
  }

  return (
    <Tabs
      activeKey={activeTab}
      onChange={handleTabChange}
      items={items}
      tabBarStyle={{
        marginBottom: 0,
      }}
      className="job-tabs [&_.ant-tabs-nav]:overflow-x-auto [&_.ant-tabs-nav]:overflow-y-hidden"
    />
  );
};

export default Job;
